import { EnumMessageRole } from '../enums/message-role.enum';
import { IChatMessage } from '../interfaces/chat-message.interface';
import { IConversationMessageView } from '../interfaces/conversation-message-view.interface';
import {
  EnumHITLDecisionType,
  EnumHITLStatus,
  IHITLConfirmationResponse,
  IHITLRequest
} from '../interfaces/hitl.interface';
import { MarkdownFormatterService } from '../services/markdown-formatter.service';
import { generateLocalId } from './local-id.util';

// O backend retorna timestamps com até microssegundos (`.020315Z`).
// O ECMA-262 só garante o parse de até milissegundos, e Safari < 15
// devolve `Invalid Date` para frações maiores. Trunca para 3 dígitos.
const parseTimestamp = (iso: string): Date =>
  new Date(iso.replace(/\.(\d{3})\d+(?=Z|[+-]\d{2}:?\d{2}$)/, '.$1'));

const safeParse = <T>(content: string): T | null => {
  try {
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
};

const describeResponse = (response: IHITLConfirmationResponse): string => {
  if (response.decision === EnumHITLDecisionType.REJECT) {
    return response.message ? `Cancelar: ${response.message}` : 'Cancelar';
  }
  return 'Aprovar';
};

const toConfirmationMessage = (api: IConversationMessageView): IChatMessage | null => {
  const interrupt = safeParse<IHITLRequest>(api.content);
  if (!interrupt) {
    return null;
  }
  return {
    id: api.id,
    localId: generateLocalId(),
    role: EnumMessageRole.AGENT,
    content: '',
    timestamp: parseTimestamp(api.timestamp),
    attachments: [],
    charts: [],
    interrupt,
    interruptResolved: false
  };
};

const toConfirmationResponseMessage = (api: IConversationMessageView): IChatMessage | null => {
  const response = safeParse<IHITLConfirmationResponse>(api.content);
  if (!response) {
    return null;
  }
  return {
    id: api.id,
    localId: generateLocalId(),
    role: EnumMessageRole.USER,
    content: describeResponse(response),
    timestamp: parseTimestamp(api.timestamp),
    attachments: [],
    charts: []
  };
};

export const toChatMessage = (
  api: IConversationMessageView,
  formatter: MarkdownFormatterService
): IChatMessage => {
  const role = api.role === 'user' ? EnumMessageRole.USER : EnumMessageRole.AGENT;

  const message: IChatMessage = {
    id: api.id,
    localId: generateLocalId(),
    role,
    content: api.content,
    timestamp: parseTimestamp(api.timestamp),
    isError: api.isError === true,
    attachments: api.attachments || [],
    charts: api.charts || []
  };

  if (api.quickActionTitle) {
    message.quickActionTitle = api.quickActionTitle;
  }

  if (api.references?.length) {
    message.references = api.references;
  }

  if (role === EnumMessageRole.AGENT && api.content) {
    message.htmlContent = formatter.format(api.content);
  }

  return message;
};

/**
 * Monta as mensagens renderizáveis do histórico, tratando os tipos HITL:
 * `confirmation` vira um card embutido e `confirmation_response` vira o bubble
 * do usuário com a decisão. Cards com resposta correspondente (mesmo
 * `interrupt_id`) são marcados como resolvidos.
 */
export const buildHistoryMessages = (
  views: IConversationMessageView[],
  formatter: MarkdownFormatterService
): IChatMessage[] => {
  const resolvedStatusById = new Map<string, EnumHITLStatus>();

  for (const view of views) {
    if (view.type === 'confirmation_response') {
      const response = safeParse<IHITLConfirmationResponse>(view.content);
      if (response && response.status !== EnumHITLStatus.PENDING) {
        resolvedStatusById.set(response.interrupt_id, response.status);
      }
    }
  }

  const messages: IChatMessage[] = [];

  for (const view of views) {
    if (view.type === 'confirmation') {
      const message = toConfirmationMessage(view);
      const resolvedStatus = message?.interrupt
        ? resolvedStatusById.get(message.interrupt.interrupt_id)
        : undefined;
      if (message?.interrupt && resolvedStatus) {
        message.interrupt = { ...message.interrupt, status: resolvedStatus };
        message.interruptResolved = true;
        messages.push(message);
      }
      continue;
    }

    if (view.type === 'confirmation_response') {
      const message = toConfirmationResponseMessage(view);
      if (message) {
        messages.push(message);
      }
      continue;
    }

    messages.push(toChatMessage(view, formatter));
  }

  return messages;
};
